import { useCallback, useState } from 'react';
import { useQuery } from '@apollo/client';

import { useAppSelector } from '@global/store/hooks';
import { userSelector } from '@global/store/slices/user/selectors';
import { countriesSelector } from '@global/store/slices/country/countrySelector';
import {
  billingInfoSelector,
  isGuestOrderSelector,
  shippingInfoSelector,
} from '@global/store/slices/cart/selectors';

import {
  AddressFieldToCompare,
  findAddressByUid,
  isAddressesEqual,
} from './helper';
import { CountryService } from '@global/utils/CountryService';

import { Address, AddressType } from '@global/types/types';
import {
  GET_SAVED_CUSTOMER_ADDRESSES,
  GET_SAVED_CUSTOMER_ADDRESSES_RESPONSE,
  GET_SAVED_CUSTOMER_ADDRESSES_VARIABLES,
} from '@global/requests/checkout/GET_SAVED_CUSTOMER_ADDRESSES';
import { SiteConfig } from '@global/utils/SiteConfig';

type SavedCustomerAddresses = {
  savedCustomerAddresses: Address[];
  selectedAddress: Address | null;
  selectedAddressUid: string;
  isNewAddress: boolean;

  // action;
  editAddress: (addressUid: string) => void;
  selectAddress: (addressUid: string) => void;
  createNewAddress: () => void;
  checkIfAddressExists: (
    address: Pick<Address, AddressFieldToCompare>
  ) => boolean;
};

type SavedCustomerAddressesHook = (
  type: AddressType,
  filter?: (address?: Address) => boolean
) => SavedCustomerAddresses;

export const useSavedCustomerAddresses: SavedCustomerAddressesHook = (
  type,
  filter = () => true
) => {
  const customer = useAppSelector(userSelector);
  const countries = useAppSelector(countriesSelector);
  const billingInfo = useAppSelector(billingInfoSelector);
  const shippingInfo = useAppSelector(shippingInfoSelector);
  const isGuestOrder = useAppSelector(isGuestOrderSelector);

  const [savedCustomerAddresses, setSavedCustomerAddresses] = useState<
    Address[]
  >([]);
  const [selectedAddress, setSelectedAddress] = useState<null | Address>(null);
  const [selectedAddressUid, setSelectedAddressUid] = useState<string>(null);
  const [isNewAddress, setIsNewAddress] = useState<boolean>(true);

  const modifyAddress = useCallback(
    ({ wCountry, ...address }: Address) => ({
      ...address,
      wCountry: CountryService.getCountryByIsoCode(countries, wCountry)
        ?.isoCodeTwo,
    }),
    [countries]
  );

  const { data: savedCustomerDataResponse } = useQuery<
    GET_SAVED_CUSTOMER_ADDRESSES_RESPONSE,
    GET_SAVED_CUSTOMER_ADDRESSES_VARIABLES
  >(GET_SAVED_CUSTOMER_ADDRESSES, {
    variables: {
      customerUid: customer.uid,
      type,
      siteId: SiteConfig.getConfig().siteId,
    },
    onError: (error) => {
      console.error('savedCustomerAddresses', error);
    },
    onCompleted: (data) => {
      const savedAddresses = data.wGetSavedAddresses?.data;
      if (!savedAddresses.length) {
        return;
      }

      const savedCustomerAddresses = savedAddresses
        .map(modifyAddress)
        .filter(filter);
      setSavedCustomerAddresses(savedCustomerAddresses);

      if (type === AddressType.BILLING && billingInfo.uid) return;
      if (type === AddressType.SHIPPING && shippingInfo.uid) return;

      const defaultAddress = savedCustomerAddresses?.[0];

      if (!defaultAddress) return;
      setSelectedAddressUid(defaultAddress.uid);
      setSelectedAddress(defaultAddress);
      setIsNewAddress(false);
    },
    skip: isGuestOrder,
  });

  const editAddress = (addressUid: string) => {
    setSelectedAddressUid(null);
    const address = findAddressByUid(savedCustomerDataResponse, addressUid);

    if (!address) return;
    setIsNewAddress(true);

    setSelectedAddress(address);
  };

  const selectAddress = (addressUid: string) => {
    const address = findAddressByUid(savedCustomerDataResponse, addressUid);

    if (!address) return;
    setIsNewAddress(false);

    setSelectedAddressUid(addressUid);
    setSelectedAddress(address);
  };

  const createNewAddress = () => {
    setIsNewAddress(true);

    setSelectedAddressUid(null);
    setSelectedAddress(null);
  };

  const checkIfAddressExists = (
    address: Pick<Address, AddressFieldToCompare>
  ) => {
    const isAddressExist = savedCustomerAddresses.some((savedAddress) => {
      return isAddressesEqual(savedAddress, address);
    });

    setIsNewAddress(!isAddressExist);

    return isAddressExist;
  };

  return {
    savedCustomerAddresses,
    selectedAddress,
    selectedAddressUid,
    isNewAddress,

    // actions
    editAddress,
    selectAddress,
    createNewAddress,
    checkIfAddressExists,
  };
};
